// src/components/booking/BookingStepVehicle.js
import React, { useState, useEffect, useCallback } from 'react';
import { Car, ArrowRight, ArrowLeft, Plus, UserCircle, Info, Trash2 } from 'lucide-react';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import BookingSummary from './BookingSummary';
import GuestVehicleForm from './GuestVehicleForm';
import { calculateVehiclePrice, inferVehicleType } from '../../utils/vehiclePricing';

const BookingStepVehicle = ({ 
  serviceDetails, 
  schedulingDetails, 
  vehicleDetails, 
  setVehicleDetails, 
  onNextStep, 
  onBack, 
  onChangeService, 
  onChangeSchedule, 
  onPriceChange
}) => {
  const { user, userProfile } = useAuth();
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    make: '',
    model: '',
    year: '',
    color: '',
    licensePlate: '',
    type: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [pricingDetails, setPricingDetails] = useState(null);
  const [showPricingInfo, setShowPricingInfo] = useState(false);
  const [lastProcessedVehicleId, setLastProcessedVehicleId] = useState(null);
  
  // Save vehicle details before proceeding to next step
  const handleContinue = async () => {
    if (!vehicleDetails.vehicleId || !vehicleDetails.location) {
      // Show validation error
      alert("Please select a vehicle and enter your location.");
      return;
    }

    try {
      // Save the current booking data to localStorage for resume capabilities
      const bookingState = {
        serviceDetails: pricingDetails ? {
          ...serviceDetails,
          originalPrice: serviceDetails.originalPrice || serviceDetails.price,
          // For STANDARD or COMPACT vehicles, don't set adjustedPrice
          ...(pricingDetails.vehicleSize !== 'STANDARD' && pricingDetails.vehicleSize !== 'COMPACT' 
              ? { adjustedPrice: pricingDetails.price } 
              : {})
        } : serviceDetails,
        schedulingDetails: {
          ...schedulingDetails,
          date: schedulingDetails.date ? schedulingDetails.date.toISOString() : null
        },
        vehicleDetails,
        currentStep: 3 // Payment step
      };
      
      localStorage.setItem('pendingBooking', JSON.stringify(bookingState));
      
      // If user is logged in, save selected vehicle to their recent vehicles
      if (user && vehicleDetails.vehicleId && !vehicleDetails.isGuestCheckout) {
        // Update vehicle lastUsed timestamp
        const vehicleRef = ref(database, `users/${user.uid}/vehicles/${vehicleDetails.vehicleId}`);
        await update(vehicleRef, {
          lastUsed: new Date().toISOString()
        });
      }
      
      // Log that we're proceeding to next step
      console.log("Proceeding to payment step");
      
      // Proceed to next step
      if (onNextStep) {
        onNextStep();
      } else {
        console.error("onNextStep function not provided to BookingStepVehicle component");
      }
    } catch (err) {
      console.error('Error saving booking progress:', err);
      // Still proceed even if there's an error
      if (onNextStep) {
        onNextStep();
      }
    }
  };
  
  // Add guest continue function (after handleContinue is defined)
  const handleGuestContinue = useCallback(() => {
    console.log("Guest continue button clicked");
    
    // Check if we have the minimum required information
    if (!vehicleDetails.vehicle) {
      alert("Please enter your vehicle information");
      return;
    }
    
    if (!vehicleDetails.location || vehicleDetails.location === "Please enter your address") {
      alert("Please enter your service location");
      return;
    }
    
    // Call the normal continue function
    handleContinue();
  }, [vehicleDetails]);
  
  // Calculate price based on selected vehicle - with check to prevent infinite loops
  useEffect(() => {
    // Only recalculate if we have all necessary data and the vehicle has changed
    if (serviceDetails && vehicleDetails?.vehicle && 
        vehicleDetails.vehicleId !== lastProcessedVehicleId) {
      
      // Record this vehicle ID as processed to prevent recalculation loops
      setLastProcessedVehicleId(vehicleDetails.vehicleId);
      
      // IMPORTANT: Always use the original base price from serviceDetails.price
      // Not the adjusted price that might already be set
      const basePrice = serviceDetails.originalPrice || serviceDetails.price;
      
      // Calculate price based on vehicle size
      const priceInfo = calculateVehiclePrice(basePrice, vehicleDetails.vehicle);
      
      // Only apply markup for non-standard vehicles
      if (priceInfo.vehicleSize === 'STANDARD' || priceInfo.vehicleSize === 'COMPACT') {
        // For STANDARD or COMPACT vehicles, don't apply any markup
        priceInfo.price = basePrice;
        priceInfo.multiplier = 1;
      }
      
      setPricingDetails(priceInfo);
      
      // Notify parent component about price change only if price actually changed
      // Compare with the original base price, not the potentially already adjusted price
      if (onPriceChange && priceInfo.price !== basePrice) {
        onPriceChange(priceInfo.price, priceInfo);
      } else if (onPriceChange && serviceDetails.adjustedPrice !== basePrice) {
        // Reset to original price if needed
        onPriceChange(basePrice, null);
      }
    } else if (!vehicleDetails?.vehicle) {
      setPricingDetails(null);
      setLastProcessedVehicleId(null);
      
      // Reset price to base if vehicle is removed
      const basePrice = serviceDetails?.originalPrice || serviceDetails?.price;
      if (onPriceChange && serviceDetails && serviceDetails.adjustedPrice !== basePrice) {
        onPriceChange(basePrice, null);
      }
    }
  }, [serviceDetails, vehicleDetails.vehicleId, onPriceChange]);
  
  // Toggle pricing info display
  const togglePricingInfo = useCallback(() => {
    setShowPricingInfo(prev => !prev);
  }, []);
  
  // Save current booking state and redirect to login
  const handleLoginRedirect = () => {
    // Get the current URL path to determine the return URL
    const currentPath = window.location.pathname;
    
    // Save current booking state to localStorage
    const bookingState = {
      serviceDetails,
      schedulingDetails: {
        ...schedulingDetails,
        date: schedulingDetails.date ? schedulingDetails.date.toISOString() : null
      },
      vehicleDetails,
      returnPath: currentPath,
      currentStep: 2, // Vehicle step
      returnToVehicleStep: true
    };
    
    localStorage.setItem('pendingBooking', JSON.stringify(bookingState));
    
    // Redirect to login page
    window.location.href = '/login?redirect=booking';
  };

  // Save current booking state and redirect to signup
  const handleSignupRedirect = () => {
    // Get the current URL path to determine the return URL
    const currentPath = window.location.pathname;
    
    // Save current booking state to localStorage
    const bookingState = {
      serviceDetails,
      schedulingDetails: {
        ...schedulingDetails,
        date: schedulingDetails.date ? schedulingDetails.date.toISOString() : null
      },
      vehicleDetails,
      returnPath: currentPath,
      currentStep: 2, // Vehicle step
      returnToVehicleStep: true
    };
    
    localStorage.setItem('pendingBooking', JSON.stringify(bookingState));
    
    // Redirect to signup page
    window.location.href = '/register?redirect=booking&returnStep=vehicle';
  };
  
  // Fetch user's saved vehicles from Firebase
  useEffect(() => {
    const loadVehicles = async () => {
      if (user) {
        try {
          setLoading(true);
          const userVehiclesRef = ref(database, `users/${user.uid}/vehicles`);
          const snapshot = await get(userVehiclesRef);
          
          if (snapshot.exists()) {
            const vehiclesData = snapshot.val();
            // Convert object to array
            const vehiclesArray = Object.keys(vehiclesData).map(key => ({
              id: key,
              ...vehiclesData[key]
            }));
            setSavedVehicles(vehiclesArray);
            
            // If we don't have a vehicle selected and there are saved vehicles
            // Automatically select their most recently used vehicle
            if (!vehicleDetails.vehicleId && vehiclesArray.length > 0) {
              // Sort by lastUsed date if available
              const sortedVehicles = [...vehiclesArray].sort((a, b) => {
                if (a.lastUsed && b.lastUsed) {
                  return new Date(b.lastUsed) - new Date(a.lastUsed);
                }
                return a.lastUsed ? -1 : b.lastUsed ? 1 : 0;
              });
              
              // Select the most recently used vehicle
              handleVehicleSelect(sortedVehicles[0].id);
            }
          } else {
            setSavedVehicles([]);
          }
        } catch (err) {
          console.error('Error fetching vehicles:', err);
          setError('Unable to load your saved vehicles. Please try again.');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    
    loadVehicles();
  }, [user]);
  
  // Handle selecting a vehicle
  const handleVehicleSelect = useCallback((vehicleId) => {
    if (vehicleId === 'new') {
      setIsAddingNew(true);
    } else {
      setIsAddingNew(false);
      const selected = savedVehicles.find(v => v.id === vehicleId);
      
      // Infer vehicle type if not present
      if (selected && !selected.type) {
        const inferredType = inferVehicleType(selected);
        if (inferredType) {
          selected.inferredType = inferredType;
        }
      }
      
      setVehicleDetails({
        ...vehicleDetails,
        vehicleId: vehicleId,
        vehicle: {
          ...selected
        }
      });
    }
  }, [savedVehicles, vehicleDetails]);
  
  // Handle saving a new vehicle
  const handleSaveNewVehicle = async () => {
    if (!user) return;
    
    // Basic validation
    if (!newVehicle.make || !newVehicle.model || !newVehicle.year) {
      setError('Please provide make, model and year of the vehicle.');
      return;
    }
    
    try {
      setLoading(true);
      
      // Generate a unique ID for the vehicle
      const vehicleId = `vehicle_${Date.now()}`;
      
      // Infer vehicle type if not provided
      let vehicleToSave = { ...newVehicle };
      if (!vehicleToSave.type || vehicleToSave.type === '') {
        const inferredType = inferVehicleType(vehicleToSave);
        if (inferredType) {
          vehicleToSave.inferredType = inferredType;
        }
      }
      
      // Save to Firebase database
      const userVehiclesRef = ref(database, `users/${user.uid}/vehicles/${vehicleId}`);
      await set(userVehiclesRef, {
        ...vehicleToSave,
        createdAt: new Date().toISOString()
      });
      
      // Add to local state
      const savedVehicle = { 
        id: vehicleId, 
        ...vehicleToSave,
        createdAt: new Date().toISOString()
      };
      
      setSavedVehicles(prevVehicles => [...prevVehicles, savedVehicle]);
      
      // Select this new vehicle
      setVehicleDetails({
        ...vehicleDetails,
        vehicleId: vehicleId,
        vehicle: savedVehicle
      });
      
      setIsAddingNew(false);
      setNewVehicle({
        make: '',
        model: '',
        year: '',
        color: '',
        licensePlate: '',
        type: ''
      });
      setError('');
    } catch (err) {
      console.error('Error saving vehicle:', err);
      setError('Unable to save your vehicle. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle location and instructions change
  const handleLocationChange = useCallback((e) => {
    setVehicleDetails(prev => ({
      ...prev,
      location: e.target.value
    }));
  }, []);

  const handleInstructionsChange = useCallback((e) => {
    setVehicleDetails(prev => ({
      ...prev,
      instructions: e.target.value
    }));
  }, []);
  
  // Format vehicle display name
  const formatVehicleName = (vehicle) => {
    if (!vehicle) return '';
    return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  };
  
  // Handle guest checkout
  const handleGuestCheckout = useCallback(() => {
    setShowGuestForm(true);
  }, []);
  
  // Handle guest vehicle submission
  const handleGuestVehicleSubmit = useCallback((guestVehicle) => {
    console.log("Received guest vehicle submission:", guestVehicle);
    
    // Infer vehicle type if not present
    let processedVehicle = { ...guestVehicle };
    if (!processedVehicle.type || processedVehicle.type === '') {
      const inferredType = inferVehicleType(processedVehicle);
      if (inferredType) {
        processedVehicle.inferredType = inferredType;
      }
    }
    
    // Update vehicle details in state
    setVehicleDetails({
      ...vehicleDetails,
      vehicleId: processedVehicle.id,
      vehicle: processedVehicle,
      isGuestCheckout: true,
      guestContactEmail: processedVehicle.contactEmail,
      guestContactPhone: processedVehicle.contactPhone,
      // Set a default location if not already set
      location: vehicleDetails.location || ""
    });
    
    console.log("Vehicle details updated for guest checkout");
    
    // For guest users, we need to explicitly ask for location after vehicle is selected
    // Focus the location input field if it exists
    setTimeout(() => {
      const locationInput = document.querySelector('input[name="location"]');
      if (locationInput) {
        locationInput.focus();
        locationInput.scrollIntoView({ behavior: 'smooth' });
      } else {
        // If location input not found, scroll to location section
        document.getElementById('location-info')?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [vehicleDetails]);
  
  // Use address from user profile if available
  useEffect(() => {
    if (userProfile && !vehicleDetails.location) {
      const addressFromProfile = getAddressFromProfile();
      if (addressFromProfile) {
        setVehicleDetails(prev => ({
          ...prev,
          location: addressFromProfile
        }));
      }
    }
  }, [userProfile, vehicleDetails.location]);
  
  // Extract address from user profile
  const getAddressFromProfile = useCallback(() => {
    if (!userProfile) return '';
    
    const parts = [];
    if (userProfile.address) parts.push(userProfile.address);
    if (userProfile.city) parts.push(userProfile.city);
    if (userProfile.state) parts.push(userProfile.state);
    if (userProfile.zipCode) parts.push(userProfile.zipCode);
    
    return parts.join(', ');
  }, [userProfile]);

  // Pricing information section
  const renderPricingInfo = () => {
    if (!pricingDetails || !serviceDetails) return null;
    
    // Only show if price has changed AND vehicle is not standard/compact
    if (pricingDetails.price === serviceDetails.price || 
        pricingDetails.vehicleSize === 'STANDARD' || 
        pricingDetails.vehicleSize === 'COMPACT') {
      return null;
    }
    
    return (
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex justify-between items-start">
          <div className="flex">
            <Info className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-800">Price Adjustment</h4>
              <p className="text-gray-600 text-sm">
                Your price has been adjusted to <span className="font-semibold">${pricingDetails.price}</span> based on your vehicle size.
              </p>
              
              {showPricingInfo && (
                <div className="mt-2 text-xs text-gray-600">
                  <p>Base price: ${serviceDetails.originalPrice || serviceDetails.price}</p>
                  <p>Vehicle size: {pricingDetails.vehicleSize}</p>
                  <p>Description: {pricingDetails.sizeDescription}</p>
                  <p>Price multiplier: {pricingDetails.multiplier}x</p>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={togglePricingInfo}
            className="text-blue-500 text-sm hover:underline"
          >
            {showPricingInfo ? 'Hide details' : 'Show details'}
          </button>
        </div>
      </div>
    );
  };
  
  // Rendering logic based on authentication state
  if (!user) {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 mb-6">
        {/* Service and schedule summary banner */}
        <BookingSummary 
          serviceDetails={pricingDetails ? {
            ...serviceDetails,
            adjustedPrice: pricingDetails.price
          } : serviceDetails}
          schedulingDetails={schedulingDetails}
          vehicleDetails={vehicleDetails}
          showServiceChange={true}
          showScheduleChange={true}
          onChangeService={onChangeService}
          onChangeSchedule={onChangeSchedule}
        />
        
        {/* Login prompt */}
        <div className="my-10 py-8 px-6 bg-blue-50 rounded-xl text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <UserCircle className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Login Required</h3>
          <p className="text-gray-600 mb-6">
            Please log in to save your vehicle information and complete your booking.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button 
              type="button"
              onClick={handleLoginRedirect}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-all duration-300"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={handleSignupRedirect}
              className="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-all duration-300"
            >
              Create Account
            </button>
          </div>
        </div>
        
        {/* Continue as guest option */}
        {!showGuestForm ? (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleGuestCheckout}
              className="text-gray-600 hover:text-blue-600 font-medium text-sm"
            >
              Continue as guest
            </button>
            <p className="text-xs text-gray-500 mt-2">
              You can continue without an account, but your vehicle information won't be saved for future bookings.
            </p>
          </div>
        ) : (
          <div className="mt-6">
            <GuestVehicleForm onVehicleSubmit={handleGuestVehicleSubmit} />
          </div>
        )}
        
        {/* Location section for guest users */}
        {vehicleDetails.isGuestCheckout && vehicleDetails.vehicle && (
          <div id="location-info" className="mt-6 border-2 border-blue-300 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Service Location</h3>
            
            {vehicleDetails.isGuestCheckout && !vehicleDetails.location && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm text-yellow-800">
                <p className="font-medium">Please enter your service location to continue</p>
                <p>We need to know where to meet you for your service.</p>
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input 
                type="text" 
                placeholder="Where should we meet you?" 
                className={`w-full px-4 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                  vehicleDetails.isGuestCheckout && !vehicleDetails.location 
                    ? 'border-yellow-500 bg-yellow-50' 
                    : 'border-gray-300'
                }`}
                value={vehicleDetails.location || ''}
                onChange={handleLocationChange}
                name="location"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  defaultValue="Chicago"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  defaultValue="60601"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
              <textarea 
                placeholder="Parking information, gate codes, etc." 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                value={vehicleDetails.instructions || ''}
                onChange={handleInstructionsChange}
              ></textarea>
            </div>
            
            {/* Add explicit continue button for guest checkout */}
            <div className="mt-4">
              <button 
                type="button"
                className={`w-full bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center ${
                  !vehicleDetails.location ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleGuestContinue}
                disabled={!vehicleDetails.location}
              >
                Continue to Payment
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                After entering your service location, click here to proceed to payment
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 mb-6">
      {/* Service and schedule summary banner */}
      <BookingSummary 
        serviceDetails={pricingDetails ? {
          ...serviceDetails,
          adjustedPrice: pricingDetails.price
        } : serviceDetails}
        schedulingDetails={schedulingDetails}
        vehicleDetails={vehicleDetails}
        showServiceChange={true}
        showScheduleChange={true}
        onChangeService={onChangeService}
        onChangeSchedule={onChangeSchedule}
      />
      
      {/* Error message display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg">
          {error}
        </div>
      )}
      
      {/* Dynamic pricing info */}
      {renderPricingInfo()}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Vehicle Selection */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Vehicle Information</h3>
          
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p>Loading your vehicles...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Display saved vehicles */}
              {savedVehicles.length > 0 ? (
                savedVehicles.map(vehicle => (
                  <div 
                    key={vehicle.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all duration-300 ${
                      vehicleDetails.vehicleId === vehicle.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                    onClick={() => handleVehicleSelect(vehicle.id)}
                  >
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                        <Car className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{formatVehicleName(vehicle)}</p>
                        <p className="text-sm text-gray-500">
                          {vehicle.color} • {vehicle.licensePlate || 'No plate information'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No saved vehicles. Add one below.
                </div>
              )}
              
              {/* Add New Vehicle Option */}
              {!isAddingNew ? (
                <div 
                  className={`border rounded-lg p-4 cursor-pointer transition-all duration-300 ${
                    vehicleDetails.vehicleId === 'new' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => handleVehicleSelect('new')}
                >
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0">
                      <Plus className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium">Add New Vehicle</p>
                      <p className="text-sm text-gray-500">Enter your vehicle details</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg p-4 border-blue-200 bg-blue-50">
                  <h4 className="font-medium mb-2">Add New Vehicle</h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Make
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="e.g. Toyota"
                          value={newVehicle.make}
                          onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Model
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="e.g. Camry"
                          value={newVehicle.model}
                          onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Year
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="e.g. 2020"
                          value={newVehicle.year}
                          onChange={(e) => setNewVehicle({...newVehicle, year: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Color
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="e.g. Blue"
                          value={newVehicle.color}
                          onChange={(e) => setNewVehicle({...newVehicle, color: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          License Plate
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="Optional"
                          value={newVehicle.licensePlate}
                          onChange={(e) => setNewVehicle({...newVehicle, licensePlate: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Vehicle Type
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        value={newVehicle.type}
                        onChange={(e) => setNewVehicle({...newVehicle, type: e.target.value})}
                      >
                        <option value="">Select vehicle type</option>
                        <option value="sedan">Sedan</option>
                        <option value="suv">SUV</option>
                        <option value="truck">Truck</option>
                        <option value="van">Van/Minivan</option>
                        <option value="coupe">Coupe/Sports Car</option>
                        <option value="convertible">Convertible</option>
                        <option value="hatchback">Hatchback</option>
                        <option value="wagon">Wagon</option>
                        <option value="other">Other</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        This helps us accurately price our service for your vehicle size
                      </p>
                    </div>
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        type="button"
                        className="px-3 py-1.5 border border-gray-300 text-xs rounded-md"
                        onClick={() => setIsAddingNew(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded-md hover:bg-blue-600"
                        onClick={handleSaveNewVehicle}
                      >
                        Save Vehicle
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Location Information */}
        <div id="location-info">
          <h3 className="text-lg font-semibold mb-4">Service Location</h3>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
            <input 
              type="text" 
              placeholder="Where should we meet you?" 
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={vehicleDetails.location || ''}
              onChange={handleLocationChange}
              name="location"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input 
                type="text" 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                defaultValue={userProfile?.city || "Chicago"}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
              <input 
                type="text" 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                defaultValue={userProfile?.zipCode || "60601"}
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
            <textarea 
              placeholder="Parking information, gate codes, etc." 
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              value={vehicleDetails.instructions || ''}
              onChange={handleInstructionsChange}
            ></textarea>
          </div>
        </div>
      </div>
      
      <div className="mt-8 flex justify-between">
        <button 
          type="button"
          className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300 flex items-center"
          onClick={onBack}
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back
        </button>
        <button 
          type="button"
          className={`bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300 flex items-center ${
            (!vehicleDetails.vehicleId || !vehicleDetails.location) ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={handleContinue}
          disabled={!vehicleDetails.vehicleId || !vehicleDetails.location}
        >
          Continue
          <ArrowRight className="ml-2 h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default BookingStepVehicle;